/** @file Import session, reading step: a PSD (through the deps' opener) or PNG files become a draft. */
import { layerChoices } from './layerChoices';
import { pngLayerFromFile } from './pngLayerFromFile';
import type { ImportCore, ImportSession, ImportSource, ImportTarget, PickedFile } from './importTypes';

const bytesOf = async (file: PickedFile): Promise<Uint8Array<ArrayBuffer>> => new Uint8Array(await file.arrayBuffer());

/** Starts a read: a newer open or a close drops this one's answer (and closes a PSD it opened late). */
async function read(core: ImportCore, fileName: string, target: ImportTarget, load: () => Promise<ImportSource>, sourceKind: ImportSource['kind']): Promise<void> {
  core.ticket.n += 1;
  const ticket = core.ticket.n;
  core.store.setState({ kind: 'reading', fileName, sourceKind }, true);
  try {
    const source = await load();
    if (ticket !== core.ticket.n) {
      if (source.kind === 'psd') source.session.close();
      return;
    }
    core.store.setState({ kind: 'choose', draft: { source, choices: layerChoices(source), target, place: false, uploaded: new Map() } }, true);
  } catch (thrown) {
    if (ticket === core.ticket.n) core.store.setState({ kind: 'failed', draft: null, thrown }, true);
  }
}

/** openPsd / openPngs of the import session. */
export function openSources(core: ImportCore): Pick<ImportSession, 'openPsd' | 'openPngs'> {
  return {
    openPsd: (file, target) =>
      read(core, file.name, target, async () => ({ kind: 'psd', fileName: file.name, session: await core.deps.openPsd(await bytesOf(file)) }), 'psd'),
    openPngs: (files, target) =>
      read(core, files.map((f) => f.name).join(', '), target, async () => {
        const layers = [];
        for (const file of files) layers.push(await pngLayerFromFile(file.name, await bytesOf(file)));
        return { kind: 'png', files: layers };
      }, 'png'),
  };
}
